import { useLocation } from "react-router-dom";
import config from "../config";
import { debug } from "../utils/debug";
import { cornerBack } from "./StoryBookCorner/storyCorner";

const AUDIO_PRE = `${config.FilePath}Audio/`;
const IMAGE_PRE = `${config.FilePath}Image/`;
const SECONDARY_AUDIO_PRE = config.SecondaryFilePath ? `${config.SecondaryFilePath}Audio/` : null;
const SECONDARY_IMAGE_PRE = config.SecondaryFilePath ? `${config.SecondaryFilePath}Image/` : null;

/**
 * The name a child sees for a game. A game's type ("Good Guess") is also its
 * key in every community's sheets, saved scores and star rules, so it stays;
 * only what's shown changes. Spencer renamed Good Guess on 2026-10-29 (no
 * "guess" in a learning game).
 */
const GAME_DISPLAY_NAMES = {
  "Good Guess": "Spot Them All",
  // Spencer rebranded it 2026-11-02: it fits more than verbs
  "Verb Activity": "Stepping Stones",
};
const displayGameName = (gameType) => GAME_DISPLAY_NAMES[gameType] ?? gameType;

// Images that ship with the app, for every community, not from its bucket
const APP_IMAGES = {
  "Game_Type_Verb_Activity2x.png": `${process.env.PUBLIC_URL ?? ""}/game-badges/stepping-stones.svg`,
  "Game_Type_From_Me_To_You2x.png": `${process.env.PUBLIC_URL ?? ""}/game-badges/from-me-to-you.svg`,
};

const fetchImage = (fileName, dataset) => {
  if (!fileName) return "";
  if (APP_IMAGES[fileName]) return APP_IMAGES[fileName];
  const base = dataset === "b_" && SECONDARY_IMAGE_PRE ? SECONDARY_IMAGE_PRE : IMAGE_PRE;
  return `${base}${encodeURIComponent(fileName.trim())}`;
};
const fetchAudio = (fileName, dataset) => {
  if (!fileName) return "";
  const base = dataset === "b_" && SECONDARY_AUDIO_PRE ? SECONDARY_AUDIO_PRE : AUDIO_PRE;
  return `${base}${encodeURIComponent(fileName.trim())}`;
};

const urlParams = new URLSearchParams();
const useLocationParams = (addStr, removeArr) => {
  let params = useLocation().search;
  if (addStr.length !== 0 || removeArr.length !== 0) {
    return `${params}&${addStr}`;
  } else {
    return "";
  }
};

const getDifficultyValue = ({ arr, dif }) => {
  if (dif === "easy") {
    return arr[0];
  } else if (dif === "medium") {
    return arr[1];
  } else if (dif === "hard") {
    return arr[2];
  }
};

const redirectFromParams = (expectedParams) => {
  // TODO: Redirect from incorrect parameters
  // Must bring in data to know what 'incorrect parameters are

  let params = window.location?.search?.split("&");
  let paramNames = params.map((str) => {
    let newStr = str.replace("?", "");
    return newStr.split("=")[0];
  });
  let missingParams = [];
  expectedParams.forEach((param) => {
    if (!paramNames.includes(param)) {
      missingParams.push(param);
    }
  });
  let redirectPath = "";
  if (missingParams.length > 0) {
    if (missingParams.includes("difficulty")) {
      redirectPath = "/";
    } else if (
      missingParams.includes("location") &&
      config.hasLocationSelection
    ) {
      const savedTown = localStorage.getItem("kz_location");
      if (savedTown) {
        const sep = window.location.search.length > 1 ? "&" : "?";
        redirectPath = `${window.location.pathname}${window.location.search}${sep}location=${encodeURIComponent(savedTown)}`;
      } else {
        redirectPath = `/choose-location${window.location.search}`;
      }
    } else if (
      missingParams.includes("unit") ||
      missingParams.includes("game-name")
    ) {
      redirectPath = `/choose-unit${window.location.search}`;
    } else if (missingParams.includes("game-type")) {
      redirectPath = `/choose-game-type${window.location.search}`;
    }
  }
  return redirectPath;
};

const goBack = (location) => {
  // stories (Story Book Corner and its games) have their own way back
  const toCorner = cornerBack(location.pathname, location.search);
  if (toCorner) return toCorner;
  const params = getSearchParams(location);
  const unitParam = params.find((d) => d.id === "unit");
  const locationArr = config.hasLocationSelection
    ? [
        { Key: 0, pathname: "/", expectedParams: [] },
        {
          Key: 1,
          pathname: "/choose-location",
          expectedParams: ["difficulty"],
        },
        {
          Key: 2,
          pathname: "/choose-unit",
          expectedParams: ["difficulty", "location"],
        },
        {
          Key: 3,
          pathname: "/choose-game-type",
          expectedParams: ["difficulty", "location", "unit", "game-name"],
        },
        {
          Key: 4,
          pathname: "/start-game",
          expectedParams: [
            "difficulty",
            "location",
            "unit",
            "game-type",
            "game-name",
          ],
        },
      ]
    : [
        { Key: 0, pathname: "/", expectedParams: [] },
        {
          Key: 1,
          pathname: "/choose-unit",
          alternate: "/choose-story-unit",
          expectedParams: ["difficulty"],
        },
        {
          Key: 2,
          pathname: "/choose-game-type",
          expectedParams: ["difficulty", "unit", "game-name"],
        },
        {
          Key: 3,
          pathname: "/start-game",
          expectedParams: ["difficulty", "unit", "game-type", "game-name"],
        },
      ];
  let newURL = { pathname: "/", search: "" };
  let newURLKeyObject = locationArr.filter(
    (loc) => loc.pathname === location.pathname,
  );
  let newURLKey = newURLKeyObject.length > 0 ? newURLKeyObject[0].Key - 1 : 0;
  if (newURLKeyObject.length === 0) {
    newURL.pathname = "/";
  } else {
    let obj = locationArr.find((loc) => loc.Key === newURLKey);
    // If goBack would land on /choose-location but a town is saved, skip it and go home
    if (obj?.pathname === "/choose-location" && localStorage.getItem("kz_location")) {
      newURL.pathname = "/";
      newURL.search = "";
    } else if (obj?.pathname === "/choose-unit" && unitParam.value.includes("Story")) {
      newURL.pathname = obj?.alternate ?? "/";
      newURL.search = filterURLsearch(obj ?? newURLKeyObject[0], location.search);
    } else {
      newURL.pathname = obj?.pathname ?? "/";
      newURL.search = filterURLsearch(obj ?? newURLKeyObject[0], location.search);
    }
  }
  return newURL;
};

const filterURLsearch = (location, search) => {
  let searchToReturn = "?";
  if (location.expectedParams.length === 0) {
    searchToReturn = "";
  } else {
    let searchArr = search.replace("?", "").split("&"); // difficulty=1
    location.expectedParams.forEach((param) => {
      searchArr.forEach((str) => {
        if (str.includes(param)) {
          searchToReturn = searchToReturn.concat(`&${str}`);
        }
      });
    });
  }
  return searchToReturn;
};

const getSearchParams = (location) => {
  let paramArray = [];
  let splitLoc = location.search
    .replaceAll("%20", " ")
    .replaceAll("%27", "'")
    .replaceAll("%E2%80%99", "’")
    .replaceAll("%C3%A9", "é")
    .replaceAll("%C3%AB", "ë")
    .replaceAll(" & ", "|*|") // Create unique string to replace later with original content
    .replace("?", "")
    .split("&")
    .filter((s) => s.length > 0);
  splitLoc.forEach((s) => {
    let newS = s.replaceAll("|*|", " & "); // Put back original content while looking for unique string
    newS = newS.split("=");
    let modifier = "";
    if (newS[0] === "unit") {
      if (isNaN(newS[1].slice(0, 2))) {
        modifier = newS[1].slice(0, 1);
        newS[1] = newS[1].slice(1);
      } else {
        modifier = newS[1].slice(0, 2);
        newS[1] = newS[1].slice(2);
      }
    }
    paramArray.push({ id: newS[0], value: newS[1], modifier: modifier });
  });
  return paramArray;
};
const compare = (a, b) => {
  let engA = a.toString().toUpperCase();
  let engB = b.toString().toUpperCase();
  let comparison = 0;
  if (!isNaN(Number(engA)) && !isNaN(Number(engB))) {
    if (Number(engA) > Number(engB)) {
      comparison = 1;
    } else {
      comparison = -1;
    }
  } else {
    if (engA > engB) {
      comparison = 1;
    } else {
      comparison = -1;
    }
  }
  return comparison;
};
const shuffle = (array) => {
  let newArray = [...array];
  let j, x, i;
  for (i = newArray.length - 1; i > 0; i--) {
    j = Math.floor(Math.random() * (i + 1));
    x = newArray[i];
    newArray[i] = newArray[j];
    newArray[j] = x;
  }
  return newArray;
};
const placementArray = (xStart, yStart, xEnd, yEnd, xInc, yInc) => {
  let array = [];
  let x;
  let y;
  // Iterate by rows (y) first, then columns (x) for left-to-right, top-to-bottom order
  for (y = yStart; y < yEnd; y = y + yInc) {
    for (x = xStart; x < xEnd; x = x + xInc) {
      array.push({ x: x, y: y });
    }
  }
  return array;
};

const findValueFromClamp = (clampString, max) => {
  let fixedString = clampString
    .replace("clamp(", "")
    .replace(")", "")
    .replace(" ", "")
    .split(",");
  let findNum = /\d+/;
  let minValue = Number(fixedString[0].replace("px", ""));
  let variableValue = Number(fixedString[1].match(findNum)) / 100;
  let maxValue = Number(fixedString[2].replace("px", ""));
  let finalValue = max * variableValue;
  if (finalValue > maxValue) {
    finalValue = maxValue;
  } else if (finalValue < minValue) {
    finalValue = minValue;
  }
  return finalValue;
};

const findSquishName = (string) => {
  let squishName = "";
  if (string !== undefined) {
    squishName = string
      .replaceAll(" ", "")
      .replaceAll("-", "")
      .replaceAll(/[0-9]/g, "");
  }
  return squishName;
};

const capEveryWord = (string) => {
  let wordArray = string.split(" ");
  for (let i = 0; i < wordArray.length; i++) {
    wordArray[i] = wordArray[i][0].toUpperCase() + wordArray[i].substr(1);
  }
  return wordArray.join(" ");
};

// Dialect-aware vocab lookup for Denaakke/Benhti.
// For each Set in the filtered vocab, picks the highest-priority dialect
// variant available based on the town's dialect structure.
// Communities without config.dialectStructure are unaffected -- pass vocabData
// straight through via the early return.
const findSingleGameData = (
  gameTarget,
  vocabData,
  locationParam,
  dialectStructure,
) => {
  if (!dialectStructure || dialectStructure.length === 0) {
    return vocabData.filter((d) => d.subCat === gameTarget);
  }

  let gameData = vocabData.filter((d) => d.subCat === gameTarget);

  // Minto uses the Benhti dataset which has no dialect variants
  if (locationParam === "Minto") {
    return gameData;
  }

  let dLocation =
    dialectStructure.find((d) => d.display === locationParam) ||
    dialectStructure[0];

  // Collect unique SetIDs (field is SetID in Denaakke data, not Set)
  let setIDs = [];
  gameData.forEach((d) => {
    if (!setIDs.includes(d.SetID)) {
      setIDs.push(d.SetID);
    }
  });

  // Dialect field contains XML tags e.g. "<DS>setsoo</DS>" -- extract the code
  const extractDialectCode = (dialectStr) => {
    if (!dialectStr) return "";
    const match = dialectStr.match(/^<([^>]+)>/);
    return match ? match[1] : dialectStr;
  };

  // For each Set, walk the priority chain and take the first match
  let finalData = [];
  setIDs.forEach((setID) => {
    let setArray = gameData.filter((d) => d.SetID === setID);
    for (let i = 0; i < dLocation.structure.length; i++) {
      let priorityCode = dLocation.structure.find((c) => c.Key === i)?.code;
      let match = setArray.find(
        (d) => extractDialectCode(d.Dialect) === priorityCode,
      );
      if (match !== undefined) {
        finalData.push(match);
        break;
      }
      if (i === dLocation.structure.length - 1) {
        debug.warn(
          "[findSingleGameData] No dialect match found for SetID:",
          setID,
        );
      }
    }
  });

  return finalData;
};

/**
 * A unit's own rows. Sub-units can share a name across units (Red Lake's VII,
 * VAI, VTI and VTA each have an Independent), so matching the name alone gave
 * all of them every row. The menus already match both.
 */
const ownUnitRows = (rows, unit) => {
  const own = rows.filter((d) => d.homeName === unit);
  return own.length > 0 ? own : rows;
};

const findGameFromConfigAndParams = (data, params) => {
  let filteredGames = [];
  config.ParamsToFindGame.forEach((p) => {
    let arrayToFilter = filteredGames.length > 0 ? filteredGames : data;
    let param = params.find((d) => d.id === p.param);
    let value = param.value;
    let dataToCheck = p.data;
    value = value.replaceAll(" ", "").replaceAll("-", "");
    if (param.id === "game-type") {
      dataToCheck = "gameType";
    }
    arrayToFilter = arrayToFilter.filter((d) => {
      let newD = d[dataToCheck]
        .toString()
        .replaceAll(" ", "")
        .replaceAll("-", "");
      return newD === value;
    });
    filteredGames = arrayToFilter;
  });
  return filteredGames[0];
};

export {
  displayGameName,
  fetchImage,
  fetchAudio,
  // useURLParamQuery,
  urlParams,
  useLocationParams,
  redirectFromParams,
  goBack,
  compare,
  shuffle,
  placementArray,
  getSearchParams,
  findValueFromClamp,
  findSquishName,
  findSingleGameData,
  ownUnitRows,
  capEveryWord,
  findGameFromConfigAndParams,
  getDifficultyValue,
};
